import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Duration } from 'moment';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { EscalationModel } from '../../components/escalation/escalation.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { EscalationInterface } from '../../interfaces/escalation.interface';
import {
    cancelEscalationTimer,
    checkEscalationRequired,
    hydrateEscalations,
    scheduleEscalationTimer,
    submitEscalationStatus,
    submitEscalationStatusError,
    submitEscalationStatusSuccess,
    triggerShowEscalationPopup,
} from '../../state/escalation/actions/escalation.actions';
import { ConfigConstants } from '../../config/config-constants';
import { NotificationStateUtilities } from '../../state/utilities/notification-state-utlities';

/**
 * NgRx-backed implementation of {@link EscalationInterface}.
 * This service is a pure dispatcher — it holds no state and contains no business logic.
 * All logic (timer scheduling, cancellation, OData save chain, modal display) lives in
 * {@link EscalationEffects}.
 */
@Injectable({ providedIn: 'root' })
export class EscalationService implements EscalationInterface {
    public constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
    ) {
        this.store.dispatch(hydrateEscalations());
    }

    public setTimerForEscalation(notification: NotificationModel, durationPassed: Duration): void {
        this.store.dispatch(
            scheduleEscalationTimer({
                notificationId: NotificationStateUtilities.toState(notification.identifier),
                notificationType: notification.notificationType,
                elapsedHours: durationPassed.asHours(),
            }),
        );
    }

    public removeEscalationTimer(notification: NotificationModel): void {
        this.store.dispatch(
            cancelEscalationTimer({ notificationId: NotificationStateUtilities.toState(notification.identifier) }),
        );
    }

    public checkEscalationRequired(): void {
        this.store.dispatch(checkEscalationRequired());
    }

    public saveEscalationStatus(escalation: EscalationModel, notification: NotificationModel): Observable<void> {
        const result$ = this.actions$.pipe(
            ofType(submitEscalationStatusSuccess, submitEscalationStatusError),
            filter((action) => NotificationStateUtilities.compare(action.notificationId, notification.identifier)),
            take(1),
            map(() => undefined as void),
        );

        this.store.dispatch(
            submitEscalationStatus({
                escalationData: {
                    notificationId: NotificationStateUtilities.toState(escalation.notificationId),
                    messageType: escalation.messageType.value,
                    date: escalation.frontDate.value.format(ConfigConstants.formatDateNoTimezone),
                    time: escalation.frontTime.value.format(ConfigConstants.formatXSDTimeDuration),
                    escalationLText: escalation.longTextInfo.getOutModel().EscalationLtextSet,
                },
            }),
        );

        return result$;
    }

    public showEscalationPopup(notification: NotificationModel): void {
        this.store.dispatch(
            triggerShowEscalationPopup({ notificationId: NotificationStateUtilities.toState(notification.identifier) }),
        );
    }
}
